import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import TicketModal from './TicketModal';

const UpcomingBookingCard = ({ booking }) => {
  const [showTicketModal, setShowTicketModal] = useState(false);
  
  if (!booking) return null;

  const eventDate = booking?.events?.event_date ? new Date(booking.events.event_date) : null;
  const isToday = eventDate && eventDate?.toDateString() === new Date()?.toDateString();
  const isTomorrow = eventDate && 
    eventDate?.toDateString() === new Date(Date.now() + 24 * 60 * 60 * 1000)?.toDateString();

  const getTimeUntilEvent = () => {
    if (!eventDate) return '';
    
    if (isToday) return 'Today';
    if (isTomorrow) return 'Tomorrow';
    
    const diffTime = eventDate - new Date();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'Past event';
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays < 7) return `${diffDays} days`;
    if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks`;
    return `${Math.ceil(diffDays / 30)} months`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-md transition-shadow">
      {/* Event Image */}
      <div className="aspect-video bg-muted relative">
        {booking?.events?.image_url ? (
          <img
            src={booking?.events?.image_url}
            alt={booking?.events?.title || 'Event'}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Icon name="Image" size={32} className="text-muted-foreground" />
          </div>
        )}
        
        {/* Status Badge */}
        <div className={`absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(booking?.status)}`}>
          {booking?.status?.charAt(0)?.toUpperCase() + booking?.status?.slice(1)}
        </div>
        
        {/* Time until event */}
        {eventDate && (
          <div className="absolute bottom-2 left-2 bg-black/70 text-white px-2 py-1 rounded text-xs font-medium">
            {getTimeUntilEvent()}
          </div>
        )}
      </div>
      {/* Event Details */}
      <div className="p-4 space-y-3">
        <div>
          <h3 className="font-semibold text-foreground line-clamp-1">
            {booking?.events?.title || 'Untitled Event'}
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            {booking?.events?.venue_name || 'Venue TBD'}
          </p>
        </div>

        {/* Date and Time */}
        {eventDate && (
          <div className="flex items-center text-sm text-muted-foreground">
            <Icon name="Calendar" size={14} className="mr-2" />
            <span>
              {eventDate?.toLocaleDateString('en-US', {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
                year: 'numeric'
              })}
            </span>
            <Icon name="Clock" size={14} className="ml-3 mr-2" />
            <span>
              {eventDate?.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit'
              })}
            </span>
          </div>
        )}

        {/* Ticket Info */}
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center text-muted-foreground">
            <Icon name="Ticket" size={14} className="mr-2" />
            <span>
              {booking?.quantity || 1} × {booking?.event_tickets?.name || 'General'}
            </span>
          </div>
          <div className="font-semibold text-foreground">
            ${booking?.total_amount?.toFixed(2) || '0.00'}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-2 pt-2">
          <Button variant="default" size="sm" className="flex-1" asChild>
            <Link to={`/events/${booking?.event_id}`}>
              View Event
            </Link>
          </Button>
          
          {booking?.status === 'confirmed' && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowTicketModal(true)}
            >
              <Icon name="QrCode" size={14} className="mr-1" />
              Ticket
            </Button>
          )}
        </div>
      </div>
      
      {/* Ticket Modal */}
      <TicketModal 
        booking={booking} 
        isOpen={showTicketModal} 
        onClose={() => setShowTicketModal(false)} 
      />
    </div>
  );
};

export default UpcomingBookingCard;